import { useState, ChangeEvent, useEffect } from "react";
import Select, { MultiValue } from "react-select";
import { SearchMode, Shipment } from "./type-model";
import { Plane, Ship, Archive, Search, Filter, Calendar, Download, ChevronRight, ChevronLeft } from "lucide-react";
import { fetchArchieveShipments, fetchOGeneralApi } from "./archieve-service";
import { formatDateOnly } from "../../components/AWBDashboard/ActionManagement/utils";
import Loading from "../../components/AWBDashboard/Loading";
import toast from "react-hot-toast";

type Option = { label: string; value: string };

// Keeps the table area a stable height across loading, populated, and
// empty states so the page doesn't jump/collapse as data changes.
const MIN_TABLE_HEIGHT = 360;

function ArchieveTable() {
    const [showFilters, setShowFilters] = useState<boolean>(false);

    const [searchMode, setSearchMode] = useState<SearchMode>("both");
    const [searchText, setSearchText] = useState<string>("");
    const [dateFrom, setDateFrom] = useState<string>("");
    const [dateTo, setDateTo] = useState<string>("");

    const [carrier, setCarrier] = useState<Option[]>([]);
    const [origin, setOrigin] = useState<Option[]>([]);
    const [destination, setDestination] = useState<Option[]>([]);

    const [dropdownOptions, setDropdownOptions] = useState<{
        carriers: string[];
        origins: string[];
        destinations: string[];
    }>({ carriers: [], origins: [], destinations: [] });

    const [allArchivedShipments, setArchivedShipments] = useState<Shipment[]>([]);
    const [loading, setLoading] = useState(false);
    const ITEMS_PER_PAGE = 10;

    const [currentPage, setCurrentPage] = useState<number>(1);

    const totalItems = allArchivedShipments.length;
    const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);

    const startItem =
        totalItems === 0 ? 0 : (currentPage - 1) * ITEMS_PER_PAGE + 1;

    const endItem = Math.min(
        currentPage * ITEMS_PER_PAGE,
        totalItems
    );

    const paginatedShipments = allArchivedShipments.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE
    );

    const toOptions = (data: string[]): Option[] =>
        data.map(item => ({ label: item, value: item }));

    const selectStyles = {
        control: (base: any, state: any) => ({
            ...base,
            minHeight: "38px",
            borderRadius: "0.5rem",
            borderColor: state.isFocused ? "#9ca3af" : "#d1d5db",
            boxShadow: "none",
            fontFamily: "Roboto, sans-serif",
            ":hover": { borderColor: "#9ca3af" },
            paddingTop: "5px",
            paddingBottom: "5px",
        }),
        multiValue: (base: any) => ({
            ...base,
            backgroundColor: "#F8B71F22"
        }),
        multiValueLabel: (base: any) => ({
            ...base,
            color: "#333435",
            fontWeight: 500
        }),
        multiValueRemove: (base: any) => ({
            ...base,
            ":hover": {
                backgroundColor: "#F8B71F",
                color: "white"
            }
        }),
        option: (base: any, state: any) => ({
            ...base,
            backgroundColor: state.isFocused
                ? "#F8B71F22"
                : state.isSelected
                    ? "#F8B71F"
                    : "white",
            color: state.isSelected ? "white" : "#333435",
            fontFamily: "Roboto, sans-serif"
        }),
        placeholder: (base: any) => ({
            ...base,
            color: "#777779",
            fontFamily: "Roboto, sans-serif"
        })
    };

    const getOrigins = async () => {
        try {
            const originData: string[] = await fetchOGeneralApi("api/v1/archieve/origin");
            setDropdownOptions(prev => ({ ...prev, origins: originData }));
        } catch {
            setDropdownOptions(prev => ({ ...prev, origins: [] }));
        }
    };
    const getDestinations = async () => {
        try {
            const destinationData: string[] = await fetchOGeneralApi("api/v1/archieve/destination");
            setDropdownOptions(prev => ({ ...prev, destinations: destinationData }));
        } catch {
            setDropdownOptions(prev => ({ ...prev, destinations: [] }));
        }
    };

    const getCarriers = async () => {
        try {
            const carrierData: string[] = await fetchOGeneralApi("api/v1/archieve/carrier");
            setDropdownOptions(prev => ({ ...prev, carriers: carrierData }));
        } catch {
            setDropdownOptions(prev => ({ ...prev, carriers: [] }));
        }
    };

    useEffect(() => {
        getOrigins();
        getCarriers();
        getDestinations();
    }, []);

    const fetchArchivedShipments = async () => {
        try {
            setLoading(true);

            const params: any = { mode: "both" };

            if (searchText) params.search = searchText;
            if (searchMode !== "both") params.mode = searchMode;
            if (dateFrom) params.dateFrom = dateFrom;
            if (dateTo) params.dateTo = dateTo;

            if (carrier.length)
                params.carrier = carrier.map(c => c.value);

            if (origin.length)
                params.origin = origin.map(o => o.value);

            if (destination.length)
                params.destination = destination.map(d => d.value);

            const searchData: Shipment[] = await fetchArchieveShipments(
                "/api/v1/archieve",
                params
            );

            setArchivedShipments(searchData);
        } catch {
            setArchivedShipments([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const timer = setTimeout(fetchArchivedShipments, 400);
        return () => clearTimeout(timer);
    }, [searchMode, searchText, dateFrom, dateTo, carrier, origin, destination]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchMode, searchText, dateFrom, dateTo, carrier, origin, destination]);


    const handleClearFilters = () => {
        setSearchMode("both");
        setSearchText("");
        setDateFrom("");
        setDateTo("");
        setCarrier([]);
        setOrigin([]);
        setDestination([]);
    };


    const handleDownloadAllReport = async (): Promise<void> => {
        try {

            const data = allArchivedShipments;

            if (!Array.isArray(data) || data.length === 0) {
                toast.error("No data available to download");
                return;
            }

            const csvRows: string[][] = [
                ['Archived Shipments Report'],
                [''],
                [
                    'Tracking ID',
                    'Type',
                    'Carrier',
                    'Origin',
                    'Destination',
                    'Reason',
                    'Archived Date',
                    'Days Inactive'
                ],
                ...data.map(item => [
                    item.id ?? '',
                    item.type ?? '',
                    item.carrier ?? '',
                    item.origin ?? '',
                    item.destination ?? '',
                    item.reason ?? '',
                    item.archivedDate
                        ? new Date(item.archivedDate).toLocaleDateString('en-IN')
                        : '',
                    String(item.daysInactive ?? 0)
                ])
            ];

            const csvContent = csvRows
                .map(row =>
                    row.map(value => `"${String(value).replace(/"/g, '""')}"`).join(',')
                )
                .join('\n');

            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);

            const a = document.createElement('a');
            a.href = url;
            a.download = `archived_shipments_${new Date().toISOString()}.csv`;
            a.click();

            URL.revokeObjectURL(url);
            toast.success("Report downloaded successfully");
        } catch (err) {
            console.error("Download error:", err);
            toast.error("Failed to download");
        }
    };



    return (
        <div className="archivedShipmentsTableMain">
            {/* Header */}
            <div className="archivedShipmentsTableHeadingOuter">
                <div className="archivedShipmentsTableHeadingLeft">
                    <h3 className="font-semibold">Archived Shipments</h3>
                </div>
                <div className="archivedShipmentsTableHeadingRight">
                    <div className="archivedShipmentsSearch">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" />
                        <input
                            type="text"
                            placeholder="Search by tracking number..."
                            value={searchText}
                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                setSearchText(e.target.value)
                            }
                            className="form-control"
                        />
                    </div>

                    <button
                        onClick={() => setShowFilters(!showFilters)}
                        className="flex btn btnBlack"
                    >
                        <Filter className="w-4 h-4" />
                        {showFilters ? "Hide Filters" : "Show Filters"}
                    </button>
                    <button
                        onClick={handleDownloadAllReport}
                        className="btn btnYellow flex items-center gap-2"
                    >
                        <Download className="w-4 h-4" />
                        <span>Download</span>
                    </button>
                </div>
            </div>

            {/* Filters */}
            {showFilters && (
                <div className="archieveFilter">
                    <div className="mb-2">
                        <label
                            style={{ color: "#333435", fontFamily: "Roboto, sans-serif" }}
                            className="block text-sm font-medium mb-2"
                        >
                            {" "}
                            Shipment Mode{" "}
                        </label>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setSearchMode("both")}
                                style={{
                                    backgroundColor: searchMode === "both" ? "#F8B71F" : "white",
                                    color: searchMode === "both" ? "white" : "#777779",
                                    fontFamily: "Roboto, sans-serif",
                                }}
                                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium transition-colors"
                            >
                                {" "}
                                All{" "}
                            </button>
                            <button
                                onClick={() => setSearchMode("ocean")}
                                style={{
                                    backgroundColor: searchMode === "ocean" ? "#F8B71F" : "white",
                                    color: searchMode === "ocean" ? "white" : "#777779",
                                    fontFamily: "Roboto, sans-serif",
                                }}
                                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium transition-colors"
                            >
                                {" "}
                                <Ship className="w-4 h-4" /> Ocean{" "}
                            </button>
                            <button
                                onClick={() => setSearchMode("air")}
                                style={{
                                    backgroundColor: searchMode === "air" ? "#F8B71F" : "white",
                                    color: searchMode === "air" ? "white" : "#777779",
                                    fontFamily: "Roboto, sans-serif",
                                }}
                                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium transition-colors"
                            >
                                {" "}
                                <Plane className="w-4 h-4" /> Air{" "}
                            </button>{" "}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                        <input
                            type="date"
                            value={dateFrom}

                            max={dateTo || undefined}
                            onMouseDown={(e: any) => {
                                e.preventDefault();
                                e.target.showPicker();
                            }}
                            onChange={(e: any) => setDateFrom(e.target.value)}
                            className="form-control"
                        />

                        <input
                            type="date"
                            value={dateTo}
                            min={dateFrom || undefined}
                            onMouseDown={(e: any) => {
                                e.preventDefault();
                                e.target.showPicker();
                            }}
                            onChange={(e) => setDateTo(e.target.value)}
                            className="form-control"
                        />
                        <Select
                            isMulti
                            classNamePrefix="react-select"
                            isClearable
                            styles={selectStyles}
                            placeholder="Select carriers..."
                            value={carrier}
                            options={toOptions(dropdownOptions.carriers)}
                            onChange={(val: MultiValue<Option>) =>
                                setCarrier(val ? [...val] : [])
                            }
                        />

                        <Select
                            isMulti
                            classNamePrefix="react-select"
                            isClearable
                            styles={selectStyles}
                            placeholder="Select origins..."
                            value={origin}
                            options={toOptions(dropdownOptions.origins)}
                            onChange={(val: MultiValue<Option>) =>
                                setOrigin(val ? [...val] : [])
                            }
                        />

                        <Select
                            isMulti
                            classNamePrefix="react-select"
                            isClearable
                            styles={selectStyles}
                            placeholder="Select dest..."
                            value={destination}
                            options={toOptions(dropdownOptions.destinations)}
                            onChange={(val: MultiValue<Option>) =>
                                setDestination(val ? [...val] : [])
                            }
                        />


                    </div>

                    <div className="flex justify-between items-center pt-4">
                        <span className="text-sm text-gray-500">
                            {allArchivedShipments.length} shipments found
                        </span>
                        <button
                            onClick={handleClearFilters}
                            className="text-sm font-semibold text-yellow-500 hover:underline"
                        >
                            Clear All Filters
                        </button>
                    </div>
                </div>
            )}

            <div className="archivedShipmentsTableOuter">
                {/* Table */}
                <div className="archivedShipmentsTableResponsive" style={{ minHeight: MIN_TABLE_HEIGHT }}>
                    <table className="archivedShipmentsTable">
                        <thead className="bg-gray-50">
                            <tr>
                                <th>Tracking Number</th>
                                <th>Type</th>
                                <th>Carrier</th>
                                <th>Route</th>
                                <th>Archive Reason</th>
                                <th>Days Inactive</th>
                                <th>Archived Date</th>
                            </tr>
                        </thead>

                        <tbody className="divide-y">
                            {loading ? (
                                <tr>
                                    <td colSpan={7} style={{ height: MIN_TABLE_HEIGHT, padding: 0 }}>
                                        <div
                                            style={{
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                height: "100%",
                                                gap: 12,
                                            }}
                                        >
                                            <Loading />
                                        </div>
                                    </td>
                                </tr>
                            ) : allArchivedShipments.length ? (
                                paginatedShipments.map((shipment, index) => {
                                    const rowBorder = { borderBottom: "1px solid rgba(224, 224, 224, 1)" };
                                    return (
                                    <tr key={index}>
                                        <td className="font-medium" style={rowBorder}>
                                            {shipment.id}
                                        </td>
                                        <td style={rowBorder}>
                                            <span className="flex items-center gap-2">
                                                {shipment.type === "ocean" ? (
                                                    <Ship className="w-4 h-4" />
                                                ) : (
                                                    <Plane className="w-4 h-4" />
                                                )}
                                                {shipment.type === "ocean" ? "Ocean" : "Air"}
                                            </span>
                                        </td>
                                        <td style={rowBorder}>{shipment.carrier}</td>
                                        <td style={rowBorder}>
                                            {shipment.origin} → {shipment.destination}
                                        </td>
                                        <td style={rowBorder}>{shipment.reason}</td>
                                        <td style={rowBorder}>
                                            {shipment.daysInactive} days
                                        </td>
                                        <td style={rowBorder}>
                                            {formatDateOnly(shipment.archivedDate)}
                                        </td>
                                    </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan={7} style={{ height: MIN_TABLE_HEIGHT }} className="px-6 text-center">
                                        <Archive className="mx-auto w-10 h-10 opacity-50" />
                                        <p className="text-sm text-gray-500 mt-2">
                                            No archived shipments found
                                        </p>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {totalItems > ITEMS_PER_PAGE && (
                    <div className="flex justify-end items-center gap-4 py-3 px-2 text-sm text-gray-600">
                        {/* Range text */}
                        <span>
                            {startItem}–{endItem} of {totalItems}
                        </span>

                        {/* Prev */}
                        <button
                            onClick={() => setCurrentPage(p => p - 1)}
                            disabled={currentPage === 1}
                            className="p-1 disabled:opacity-40"
                        >
                            <ChevronLeft size={18} />
                        </button>

                        {/* Next */}
                        <button
                            onClick={() => setCurrentPage(p => p + 1)}
                            disabled={currentPage === totalPages}
                            className="p-1 disabled:opacity-40"
                        >
                            <ChevronRight size={18} />
                        </button>
                    </div>
                )}


            </div>


        </div>
    );
}

export default ArchieveTable;